import { Taxonomy } from './types';
import {
    envBoolean,
    envId,
    envString,
} from './utils/env.utils';

// APP CONFIG
export const ADMIN_URL = envString('REACT_APP_ADMIN_URL');
export const API_URL = ADMIN_URL + '/api';
export const DEBUG_MODE: boolean = envBoolean('REACT_APP_DEBUG_MODE');
export const LOCAL_STORAGE_DATA_NAME: string = 'GenderQuotaDashboardSession';
export const INTERPOLATION_CHARS: [string, string] = ['{{', '}}'];

// ROUTER PATHS
export const ROUTE_PAGE_HOME = '/';
export const ROUTE_PAGE_COUNTRY_PROFILE = '/country-profiles';
export const ROUTE_PAGE_QUOTA_ANALYSIS = '/quota-analysis';
export const ROUTE_PAGE_COUNTRY_COMPARISON = '/country-comparison';
export const ROUTE_PAGE_ABOUT = '/about';
export const ROUTE_PAGE_TERMS_OF_USE = '/terms-of-use';
export const ROUTE_PAGE_PRIVACY_NOTICE = '/privacy-notice';

// COLORS
export const COLOR_WHITE = '#FFFFFF';
export const COLOR_BLACK = '#000000';
export const COLOR_ERROR = '#ff4b4b';
export const COLOR_01 = '#009EDB'; // UN skyblue
export const COLOR_02 = '#F9F9F9';
export const COLOR_03 = '#ABE6FA';
export const COLOR_04 = '#003858';
export const COLOR_05 = '#00466D'; // deep blue
export const COLOR_06 = '#E7F4F9'; // soft skyblue for background
export const COLOR_07 = '#56CEFB'; // light skyblue
export const COLOR_08 = '#00397B'; // repeated
export const COLOR_09 = '#99C9AC'; // table gradient 1
export const COLOR_10 = '#0075FF'; // shiny skyblue
export const COLOR_11 = '#00B6F0'; // table gradient 2
export const COLOR_12 = '#333333'; // dark gray of most texts
export const COLOR_13 = '#E3E3E3';
export const COLOR_14 = '#B1B1B1';
export const COLOR_15 = '#D4E1E5';
export const COLOR_16 = '#F3F3F3';
export const COLOR_17 = '#BCC8CC';
export const COLOR_18 = '#CECECE';
export const COLOR_19 = '#C1FBD8';
export const COLOR_20 = '#6FC59B';
export const COLOR_21 = '#DAE6E9';
export const COLOR_22 = 'rgba(7, 57, 88, 0.5)';
export const COLOR_23 = 'rgba(58, 144, 192, 0.32)'; // used for shadow of boxes
export const COLOR_24 = '#428BB4';
// export const COLOR_25 = '#2F89BB';
export const COLOR_25 = '#3D82A8';
export const COLOR_26 = '#82B6D2';
export const COLOR_27 = '#56B1E4'; // for login color overlay
export const COLOR_28 = '#073958'; // for lines between charts in global statistics
export const COLOR_29 = '#00B6FD';
export const COLOR_30 = '#DBEFF6'; // for alternate global quota donut chart
// Green palette
export const COLOR_31 = '#3E9F70';
export const COLOR_32 = '#64C468';
// export const COLOR_33 = '#E9F9F1';
export const COLOR_33 = '#E0F5EA';
export const COLOR_34 = '#F7FDF9';
export const COLOR_35 = '#2A8458';
// export const COLOR_36 = '#BEEDD6';
export const COLOR_36 = '#83D79F';
export const COLOR_37 = '#1F6141';
export const COLOR_38 = '#C2E7D4';

// FONTS
export const FONT_MAIN: string = '"Roboto", sans-serif';
export const FONT_WEIGHT_LIGHT: number = 300;
export const FONT_WEIGHT_REGULAR: number = 400;
export const FONT_WEIGHT_MEDIUM: number = 500;
export const FONT_WEIGHT_BOLD: number = 700;
export const FONT_WEIGHT_BLACK: number = 900;

// MOBILE DEFINITIONS (GLOBAL)
export const PAGE_MENU_HEIGHT_PX: number = 60;
export const PAGE_MENU_ZINDEX: number = 9;
export const PAGE_CONTENT_ZINDEX: number = 1;
export const PAGE_FOOTER_HEIGHT_PX: number = 240;
export const PAGE_FOOTER_ZINDEX: number = 8;
export const MODAL_ZINDEX: number = 10;
export const CONTENT_MAX_WIDTH_PX: number = 1200;
export const MARGINS_PX: number = 16;

// TABLET DEFINITIONS
export const TABLET_BREAKPOINT: number = 720;
export const TABLET_PAGE_MENU_HEIGHT_PX: number = 60;
export const TABLET_PAGE_FOOTER_HEIGHT_PX: number = 60;

// DESKTOP DEFINITIONS
export const DESKTOP_BREAKPOINT: number = 1200;
export const DESKTOP_PAGE_MENU_HEIGHT_PX: number = 90;
export const DESKTOP_PAGE_FOOTER_HEIGHT_PX: number = 88;
export const SITE_FOOTER_HEIGHT_PX_DESKTOP: number = 88;

// MESSAGES AND LABELS
export const MESSAGE_LOGIN_ERROR = 'Invalid credentials';
export const MESSAGE_NOT_AVAILABLE_IN_YEAR = 'No gender quota for selected year';
export const MESSAGE_NOT_AVAILABLE_IN_DATE = 'No gender quota for {{level}} level for selected date';
export const MESSAGE_NOT_AVAILABLE_PROFILE = 'No gender quota for {{for}}';
export const MESSAGE_NOT_AVAILABLE_INFORMATION_FOR_DATE = 'No information available for selected date';
export const MESSAGE_NOT_AVAILABLE_INFORMATION_FOR_COUNTRY = 'No information available for {{country}} for selected date';
export const MESSAGE_NO_RESULTS = 'No result for this search';
export const LABEL_FOR_CURRENT_DATE = 'Current';
export const LABEL_FOR_COUNTRIES_SEARCH_FIELD = 'Search for a country...';

// KNOWN IDS
export const LEGAL_SOURCE_TRUE_ID: number = envId('REACT_APP_LEGAL_SOURCE_TRUE_ID');
export const LEGAL_SOURCE_FALSE_ID: number = envId('REACT_APP_LEGAL_SOURCE_FALSE_ID');
export const RANKING_PLACEMENT_NO_ID: number = envId('REACT_APP_RANKING_PLACEMENT_NO_ID');
export const RANKING_PLACEMENT_NA_ID: number = envId('REACT_APP_RANKING_PLACEMENT_NA_ID');
export const SANCTIONS_NO_ID: number = envId('REACT_APP_SANCTIONS_NO_ID');
export const SANCTIONS_NA_ID: number = envId('REACT_APP_SANCTIONS_NA_ID');
export const QUOTA_TYPE_CANDIDATE_QUOTA_ID: number = envId('REACT_APP_QUOTA_TYPE_CANDIDATE_QUOTA_ID');
export const QUOTA_TYPE_RESERVED_SEATS_ID: number = envId('REACT_APP_QUOTA_TYPE_RESERVED_SEATS_ID');

// HARDCODED DATA
export const COUNTRY_COMPARISON_RESULTS_MAX_SELECTION: number = 5;
export const COUNTRY_COUNT: number = 193;
export const COUNTRY_COUNT_AMERICAS_AND_CARIBBEAN: number = 193;
export const COUNTRY_COUNT_ARAB_STATES: number = 193;
export const COUNTRY_COUNT_ASIA_AND_PACIFIC: number = 193;
export const COUNTRY_COUNT_EAST_AND_SOUTHERN_AFRICA: number = 193;
export const COUNTRY_COUNT_EUROPE_AND_CENTRAL_ASIA: number = 193;
export const COUNTRY_COUNT_WEST_AND_CENTRAL_AFRICA: number = 193;
export const COUNTRY_COUNT_ASIA: number = 48;
export const COUNTRY_COUNT_OCEANIA: number = 14;
export const COUNTRY_COUNT_AFRICA: number = 54;
export const COUNTRY_COUNT_EUROPE: number = 43;
export const COUNTRY_COUNT_AMERICAS: number = 35;
export const NOT_APPLICABLE_TEXT: string = 'N/A';
export const YES_TEXT: string = "Yes";
export const NO_TEXT: string = "No";
export const QUOTA_TYPE_EMPTY_TEXT: string = 'None';
export const DATETIME_1_JAN_2022: number = 1641013200000;
export const TAXONOMIES_LEGAL_SOURCES: Taxonomy[] = [
    {id: 1, name: 'Constitution', prefix: null},
    {id: 2, name: 'Electoral law', prefix: null},
    {id: 7, name: 'Local government law', prefix: null},
    {id: 3, name: 'Parity law', prefix: null},
    {id: 4, name: 'Party law', prefix: null},
    {id: 5, name: 'Financing law', prefix: null},
    {id: 6, name: 'Political agreement', prefix: null},
];
export const MONTH: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];
